import { FiltersData, FilterContents } from "@/models/filter";
import { Period } from "@/utils/shared-variables";
import { normalizeElasticsearchTypeToFilterType } from "@/utils/normalize-es-types";
import logger from "@/utils/logger";

/**
 * Type étendu pour les périodes supportées
 * Inclut les anciennes valeurs (1day, 1week, etc.) et les nouvelles (last_7_days, etc.)
 */
type ExtendedPeriod = Period | "last_7_days" | "last_30_days" | "last_90_days";

/**
 * Convertit une période en plage de dates
 * Supporte les deux formats : ancien (1day, 1week...) et nouveau (last_7_days, last_30_days...)
 */
const getDateRangeForPeriod = (period: ExtendedPeriod): { start: Date; end: Date } => {
  const now = new Date();
  const end = new Date(now);
  let start = new Date(now);

  switch (period) {
    // Ancien format
    case "1day":
      start.setDate(start.getDate() - 1);
      break;
    case "1week":
      start.setDate(start.getDate() - 7);
      break;
    case "1month":
      start.setMonth(start.getMonth() - 1);
      break;
    case "3month":
      start.setMonth(start.getMonth() - 3);
      break;
    case "1year":
      start.setFullYear(start.getFullYear() - 1);
      break;
    // Nouveau format (utilisé par HierarchicalFilters)
    case "last_7_days":
      start.setDate(start.getDate() - 7);
      break;
    case "last_30_days":
      start.setDate(start.getDate() - 30);
      break;
    case "last_90_days":
      start.setDate(start.getDate() - 90);
      break;
  }

  return { start, end };
};

/**
 * Échappe une valeur pour la query string Elasticsearch
 */
const escapeValue = (value: any): string => {
  if (value === null || value === undefined) {
    return "";
  }
  
  const str = String(value);
  
  // Si la valeur contient des espaces ou des caractères spéciaux, l'entourer de guillemets
  if (str.includes(" ") || str.includes(":") || str.includes("(") || str.includes(")") || str.includes("\"")) {
    return `"${str.replace(/"/g, '\\"')}"`;
  }
  
  return str;
};

/**
 * Convertit un filtre en clause de query string Elasticsearch
 */
const convertFilterToQueryStringClause = (
  filter: FilterContents,
  availableFilters?: Array<{ key: string; type: string }>
): string | null => {
  if (!filter.filter?.key || !filter.filter.operator) {
    return null;
  }

  const { key, operator, value } = filter.filter;
  const esType = availableFilters?.find((f) => f.key === key)?.type || "text";
  // Normaliser le type ES vers le type de filtre pour les comparaisons
  const filterType = normalizeElasticsearchTypeToFilterType(esType);

  // Correction automatique: si operator="range" mais value est une string (probablement une période)
  // et que le type est "date", changer l'opérateur en "period"
  if (operator === "range" && typeof value === "string" && filterType === "date") {
    // Traiter comme une période
    const dateRange = getDateRangeForPeriod(value as ExtendedPeriod);
    const startISO = dateRange.start.toISOString().split("T")[0];
    const endISO = dateRange.end.toISOString().split("T")[0];
    return `${key}:[${startISO} TO ${endISO}]`;
  }

  // Debug: logger les filtres invalides restants
  if (operator === "range" && (typeof value === "string" || value === null || value === undefined)) {
    logger.warn(`Filter invalide: operator="range" mais value n'est pas un objet avec min/max`, { key, operator, value, filterType });
    return null;
  }

  // Clause pour les dates avec période
  if (operator === "period" && typeof value === "string") {
    const dateRange = getDateRangeForPeriod(value as ExtendedPeriod);
    const startISO = dateRange.start.toISOString().split("T")[0];
    const endISO = dateRange.end.toISOString().split("T")[0];
    return `${key}:[${startISO} TO ${endISO}]`;
  }

  // Clause pour les dates avec plage personnalisée
  if (operator === "date" && value && typeof value === "object" && "dateStart" in value) {
    const dateValue = value as { dateStart: Date | null; dateEnd: Date | null };
    const parts: string[] = [];
    
    if (dateValue.dateStart) {
      const startISO = dateValue.dateStart.toISOString().split("T")[0];
      parts.push(startISO);
    } else {
      parts.push("*");
    }
    
    if (dateValue.dateEnd) {
      const endISO = dateValue.dateEnd.toISOString().split("T")[0];
      parts.push(endISO);
    } else {
      parts.push("*");
    }
    
    return `${key}:[${parts[0]} TO ${parts[1]}]`;
  }

  // Clause pour les ranges numériques
  if (operator === "range") {
    // Si la valeur est un objet avec min/max
    if (value && typeof value === "object" && ("min" in value || "max" in value)) {
      const rangeValue = value as { min?: number; max?: number };
      
      // Si on a à la fois min et max, utiliser la syntaxe [min TO max]
      if (rangeValue.min !== undefined && rangeValue.max !== undefined) {
        return `${key}:[${rangeValue.min} TO ${rangeValue.max}]`;
      }
      // Si on a seulement min, utiliser >=
      if (rangeValue.min !== undefined) {
        return `${key}:>=${rangeValue.min}`;
      }
      // Si on a seulement max, utiliser <=
      if (rangeValue.max !== undefined) {
        return `${key}:<=${rangeValue.max}`;
      }
    }
    // Si la valeur n'est pas un objet valide, retourner null (filtre invalide)
    return null;
  }

  // Clause pour "anyof" (terms) - utiliser OR
  if (operator === "anyof" && Array.isArray(value) && value.length > 0) {
    const terms = value.map((v) => `${key}:${escapeValue(v)}`).join(" OR ");
    return `(${terms})`;
  }

  // Clause pour "=" (term)
  if (operator === "=") {
    if (filterType === "boolean") {
      const boolValue = value === "true" || value === true;
      return `${key}:${boolValue}`;
    }
    return `${key}:${escapeValue(value)}`;
  }

  // Clause pour sentiment rangeAnyOf
  // Sentiment is stored as text values ("positive", "negative", "neutral")
  if (operator === "rangeAnyOf" && Array.isArray(value)) {
    const clauses = value
      .filter((v) => ["positive", "negative", "neutral"].includes(v))
      .map((v) => `${key}:${v}`);
    
    if (clauses.length > 0) {
      return `(${clauses.join(" OR ")})`;
    }
  }

  return null;
};

/**
 * Convertit FiltersData en query string Elasticsearch
 * WHERE est traité comme une parenthèse ouvrant un groupe de filtres
 */
export const convertFiltersToQueryString = (
  filtersData: FiltersData,
  availableFilters?: Array<{ key: string; type: string }>
): string => {
  const clauses: string[] = [];

  // Ajouter le filtre par tracker IDs directement dans la query string
  if (filtersData.trackers_id) {
    const ids = Array.isArray(filtersData.trackers_id)
      ? filtersData.trackers_id
      : [filtersData.trackers_id];
    if (ids.length > 0) {
      // Utiliser le champ ES avec le préfixe de type
      const trackerClause = `es_metadata.tracker.text$id.keyword:(${ids.join(" OR ")})`;
      clauses.push(trackerClause);
    }
  }

  // Ajouter la recherche textuelle si présente
  if (filtersData.search && filtersData.search.trim()) {
    clauses.push(`(${filtersData.search})`);
  }

  // Convertir les filtres en groupes basés sur WHERE
  if (!filtersData.filters || filtersData.filters.length === 0) {
    const queryString = clauses.join(" ").trim();
    return queryString || "";
  }

  // Structure pour gérer les groupes imbriqués
  type GroupNode = {
    clauses: string[];
    operator?: string;
    children: GroupNode[];
  };

  const rootGroups: GroupNode[] = [];
  const groupStack: GroupNode[] = []; // Pile pour gérer l'imbrication
  let currentGroup: GroupNode | null = null;

  filtersData.filters.forEach((filter, index) => {
    const clause = convertFilterToQueryStringClause(filter, availableFilters);
    if (!clause) {
      // Si la clause est null, le filtre est invalide, on l'ignore
      return;
    }

    const booleanOperator = filter.booleanOperator || "WHERE";

    // Si on rencontre un WHERE, cela marque le début d'un nouveau groupe (imbriqué ou non)
    if (booleanOperator === "WHERE") {
      // Créer un nouveau groupe
      const newGroup: GroupNode = {
        clauses: [clause],
        operator: filter.groupOperator || (index > 0 ? "AND" : undefined),
        children: []
      };

      if (currentGroup) {
        // Ajouter le nouveau groupe comme enfant du groupe actuel (imbrication)
        currentGroup.children.push(newGroup);
        // Empiler le groupe actuel pour pouvoir y revenir
        groupStack.push(currentGroup);
        // Le nouveau groupe devient le groupe actuel
        currentGroup = newGroup;
      } else {
        // Nouveau groupe au niveau racine
        rootGroups.push(newGroup);
        currentGroup = newGroup;
      }
    } else {
      // Pour les autres opérateurs (AND, OR, etc.), ajouter au groupe en cours
      if (!currentGroup) {
        // Si pas de groupe en cours, créer un groupe racine implicite
        currentGroup = {
          clauses: [clause],
          children: []
        };
        rootGroups.push(currentGroup);
      } else {
        // Ajouter l'opérateur et la clause au groupe en cours
        switch (booleanOperator) {
          case "AND":
            currentGroup.clauses.push(`AND ${clause}`);
            break;
          case "OR":
            currentGroup.clauses.push(`OR ${clause}`);
            break;
          case "AND NOT":
            currentGroup.clauses.push(`AND NOT ${clause}`);
            break;
          case "OR NOT":
            currentGroup.clauses.push(`OR NOT ${clause}`);
            break;
          default:
            currentGroup.clauses.push(`AND ${clause}`);
            break;
        }
      }
    }
  });

  // Fonction récursive pour convertir un groupe en string
  const groupToString = (group: GroupNode, isFirst: boolean = false): string => {
    const parts: string[] = [];
    
    // Ajouter les clauses du groupe
    if (group.clauses.length > 0) {
      parts.push(group.clauses.join(" "));
    }
    
    // Ajouter les groupes enfants (imbriqués)
    group.children.forEach((child, index) => {
      const childString = groupToString(child, index === 0 && parts.length === 0);
      if (childString) {
        // Utiliser l'opérateur du groupe enfant pour le joindre
        const operator = child.operator || "AND";
        if (parts.length > 0 || index > 0) {
          parts.push(`${operator} ${childString}`);
        } else {
          parts.push(childString);
        }
      }
    });
    
    // Si le groupe a des clauses ou des enfants, l'entourer de parenthèses
    if (parts.length > 0) {
      const content = parts.join(" ");
      // Si c'est le premier groupe racine et qu'il n'y a qu'un seul groupe, ne pas ajouter de parenthèses externes
      if (isFirst && rootGroups.length === 1 && group.children.length === 0) {
        return content;
      }
      return `(${content})`;
    }
    
    return "";
  };

  // Convertir tous les groupes racine en string
  const filterClauses = rootGroups.map((group, index) => {
    const groupString = groupToString(group, index === 0);
    if (index === 0) {
      return groupString;
    }
    const operator = group.operator || "AND";
    return `${operator} ${groupString}`;
  }).filter(Boolean).join(" ");
  
  // Ajouter les clauses de filtres aux clauses existantes (recherche textuelle)
  if (filterClauses) {
    if (clauses.length > 0) {
      clauses.push(`AND ${filterClauses}`);
    } else {
      clauses.push(filterClauses);
    }
  }

  const queryString = clauses.join(" ").trim();
  
  return queryString || "";
};

